import { SortingQueryType } from './SortingDashboardTypes';

export enum NoUpdateStatusInput {
    CURRENTLY = 'CURRENTLY',
    EVER_NOT_CURRENT = 'EVER_NOT_CURRENT',
    BEFORE_PENALTY = 'BEFORE_PENALTY',
    ALL = 'ALL',
}

export enum NoUpdateDashboardDrillTypeInput {
    TOTAL = 'TOTAL',
    IN_HUB = 'IN_HUB',
    LINEHAUL_INBOUND = 'LINEHAUL_INBOUND',
    IN_HUB_OPERATION = 'IN_HUB_OPERATION',
    IN_HUB_LINEHAUL = 'IN_HUB_LINEHAUL',
    LAST_MILE = 'LAST_MILE',
    LAST_MILE_AT_WHS = 'LAST_MILE_AT_WHS',
    LAST_MILE_AT_DSP = 'LAST_MILE_AT_DSP',
}

export enum NoUpdateTypeGql {
    LINEHAUL_INBOUND = 'LINEHAUL_INBOUND',
    IN_HUB_OPERATION = 'IN_HUB_OPERATION',
    IN_HUB_LINEHAUL = 'IN_HUB_LINEHAUL',
    LAST_MILE = 'LAST_MILE',
}

export enum NoUpdateLocationTypeGql {
    HUB = 'HUB',
    WHS = 'WHS',
    DSP = 'DSP',
}

export interface NoUpdateDashboardQueryInput {
    queryType: SortingQueryType;
    startDate: string;
    endDate: string;
    hubId: number;
    noUpdateStatus?: NoUpdateStatusInput;
}

export interface LastMileWhsBreakdownItem {
    responsibleWarehouseId: number;
    sortingCode?: string | null;
    count: number;
    rate?: string | null;
    /** 该子项独立分母（按 responsibleWarehouseId 维度去重），用于"两期分母均>1000"标红门槛 */
    parcelCount?: number;
}

export interface InHubLinehaulHubBreakdownItem {
    responsibleHubId: number;
    sortingCode?: string | null;
    count: number;
    rate?: string | null;
    /** 该子项独立分母（按 responsibleHubId 维度去重），用于"两期分母均>1000"标红门槛 */
    parcelCount?: number;
}

export interface NoUpdateDashboardItem {
    timePeriod: string;
    hubId: number;
    sortingCode?: string | null;
    linehaulInboundCount: number;
    inHubOperationCount: number;
    inHubLinehaulCount: number;
    lastMileCount: number;
    lastMileAtWhsCount: number;
    lastMileAtDspCount: number;
    linehaulInboundParcelCount: number;
    inHubOperationParcelCount: number;
    inHubLinehaulParcelCount: number;
    lastMileParcelCount: number;
    /** 数仓去重分子分母：仅 inHubNoUpdateRate / totalNoUpdateRate 用（率取后端，这 4 列供下钻判定 + 标红门槛） */
    inHubNoUpdateCount: number;
    inHubParcelCount: number;
    totalNoUpdateCount: number;
    totalParcelCount: number;
    totalNoUpdateRate?: string | null;
    inHubNoUpdateRate?: string | null;
    linehaulInboundRate?: string | null;
    inHubOperationRate?: string | null;
    inHubLinehaulRate?: string | null;
    lastMileRate?: string | null;
    lastMileAtWhsRate?: string | null;
    lastMileAtDspRate?: string | null;
    lastMileByResponsibleWhs: LastMileWhsBreakdownItem[];
    inHubLinehaulByResponsibleHub: InHubLinehaulHubBreakdownItem[];
    linehaulInboundByResponsibleHub: InHubLinehaulHubBreakdownItem[];
    inHubOperationByResponsibleHub: InHubLinehaulHubBreakdownItem[];
}

export interface NoUpdateDashboardAggregateItem {
    hubId: number;
    sortingCode?: string | null;
    linehaulInboundCount: number;
    inHubOperationCount: number;
    inHubLinehaulCount: number;
    lastMileCount: number;
    lastMileAtWhsCount: number;
    lastMileAtDspCount: number;
    linehaulInboundParcelCount: number;
    inHubOperationParcelCount: number;
    inHubLinehaulParcelCount: number;
    lastMileParcelCount: number;
    /** 数仓去重分子分母：仅 inHubNoUpdateRate / totalNoUpdateRate 用（率取后端，这 4 列供下钻判定 + 标红门槛） */
    inHubNoUpdateCount: number;
    inHubParcelCount: number;
    totalNoUpdateCount: number;
    totalParcelCount: number;
    totalNoUpdateRate?: string | null;
    inHubNoUpdateRate?: string | null;
    linehaulInboundRate?: string | null;
    inHubOperationRate?: string | null;
    inHubLinehaulRate?: string | null;
    lastMileRate?: string | null;
    lastMileAtWhsRate?: string | null;
    lastMileAtDspRate?: string | null;
    lastMileByResponsibleWhs: LastMileWhsBreakdownItem[];
    inHubLinehaulByResponsibleHub: InHubLinehaulHubBreakdownItem[];
    linehaulInboundByResponsibleHub: InHubLinehaulHubBreakdownItem[];
    inHubOperationByResponsibleHub: InHubLinehaulHubBreakdownItem[];
}

export interface NoUpdateDashboardResult {
    items: NoUpdateDashboardItem[];
    aggregate: NoUpdateDashboardAggregateItem[];
    latestCalculatedAt?: string | null;
}

export interface QueryNoUpdateDashboardResponse {
    queryQualityDashboard: NoUpdateDashboardResult;
}

// ---------- 明细 ----------

export interface NoUpdateDetailPageRequestInput {
    statDateStart: string;
    statDateEnd: string;
    hubId: number;
    hubIds?: number[] | null;
    drillType: NoUpdateDashboardDrillTypeInput;
    noUpdateStatus?: NoUpdateStatusInput;
    page?: number;
    size?: number;
    sortBy?: string | null;
    sortDirection?: 'ASC' | 'DESC' | null;
    responsibleHubId?: number | null;
    responsibleWarehouseId?: number | null;
}

export interface NoUpdateDetailRow {
    id: string;
    trackingNumber: string;
    bagNumber?: string | null;
    orderId?: number | null;
    hubId: number;
    hubName?: string | null;
    sortingCode?: string | null;
    noUpdateType: NoUpdateTypeGql;
    noUpdateLocationType: NoUpdateLocationTypeGql;
    /** 断更地点 id（warehouse/dsp id），用于按地点筛选 */
    noUpdateLocationId: number;
    noUpdateLocationName?: string | null;
    noUpdateHoursBucket: number;
    responsibleHubName?: string | null;
    responsibleWarehouseName?: string | null;
    lastTrackingEventName?: string | null;
    lastTrackingAccountName?: string | null;
    lastTrackingAccountRole?: string | null;
    lastTrackingTime?: string | null;
    noUpdateStartEventName?: string | null;
    noUpdateStartAccountName?: string | null;
    noUpdateStartAccountRole?: string | null;
    noUpdateStartTime: string;
    noUpdateEndTime?: string | null;
    noUpdateHours?: string | null;
    /** 1=当前断更中 / 0=曾断更已恢复 / -1=未到判罚时长 */
    isCurrentlyNoUpdate: number;
    isCausedPenalty: boolean;
    statDate: string;
    calculatedAt: string;
    /** 包裹状态（Order.overallStatus enum name）；非法/未关联时为 null */
    parcelStatus?: string | null;
    /** DSP 名（仅尾程链路通常有值） */
    dspName?: string | null;
    /** 司机名（仅尾程链路通常有值） */
    driverName?: string | null;
    /** 目的地线路（delivery_task_order.route_name 最新一条） */
    routeName?: string | null;
    /** 断更责任仓名称（HUB/WHS 取断更地点名称；DSP 取关联仓库名称） */
    responsibleSiteName?: string | null;
}

export interface NoUpdateDetailPageResponse {
    content: NoUpdateDetailRow[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

export interface QueryNoUpdateDetailListResponse {
    queryNoUpdateDetailList: NoUpdateDetailPageResponse;
}
